// A wrapper on http client dar intercepts requests from app
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, from, timeout } from 'rxjs';
import { PATH_LOCATIONS, PATHS, REQUEST_STATUSES } from '../../_constants/values/values.service';
import { Router } from '@angular/router';
import { ConfigService, JSONRPC } from '../../_constants/config/config.service';
import { HttpsRequestBody, XAPI_METHODS, XAPIS, XAPI_METHODS_NAMES } from '../../../models/Core.model';
import { AuthService } from '../../_app/auth/auth.service';

@Injectable({
    providedIn: 'root'
})
export class RequestsService {
    // for all requests, add the token to the headers
    constructor(
        private readonly http: HttpClient,
        private readonly router: Router,
        private readonly authService: AuthService,
        private readonly configService: ConfigService
    ) {
    }

    public get(serverUrl: string, api: XAPIS, options: any = {}): Observable<any> {
        const headers = new HttpHeaders({
            'Authorization': `Bearer ${this.authService.getToken()}`,
            'Content-Type': 'application/json'
        });
        options = { ...options, headers };
        return this.http.get(`${serverUrl}${api}`, options);
    }

    formatError(error: any): string {
        if (error?.error?.id === 751695) {
            error.error.formattedMessageId = 'organiser.error.missing_functionality';
        } else if (error?.error?.id === 545488) {
            error.error.formattedMessageId = 'organiser.error.entity_already_exists';
        } else {
            error.error.formattedMessageId = 'organiser.error.generic';
        }
        return error;
    }

    public post(serverUrl: string, api: XAPIS, body: HttpsRequestBody, options: any = {}): Observable<any> {
        const exceptions: XAPIS[] = [
            XAPIS.stock
        ];
        const requestTimeoutMs = options.requestTimeoutMs ?? 10000;
        const httpOptions = { ...options };
        delete httpOptions.requestTimeoutMs;

        if (this.configService.getDummyRequests() && exceptions.includes(api)) {
            return from(import('./dummy-requests.json')
                .then(
                    (res: any) => {
                        return res.default[api + '_' + body.method];
                    },
                    (error: any) => {
                        throw error;
                    }
                )
            );
        } else {
            if (body.method !== XAPI_METHODS_NAMES.connect || api !== XAPIS.auth) {
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${this.authService.getToken()}`,
                    'Content-Type': 'application/json'
                });
                httpOptions.headers = headers;
            }
            return this.http.post(`${serverUrl}${api}`, body, httpOptions)
                .pipe(
                    timeout(requestTimeoutMs),
                    map((resp: any) => {
                        if (resp.code === 401) {
                            this.authService.clearLoginInfo();
                            this.router.navigate([PATH_LOCATIONS.children[PATHS.LOGIN].path]);
                            return resp;
                        }
                        if (resp?.status === REQUEST_STATUSES.error) {
                            throw this.formatError(resp);
                        }
                        return resp;
                    }),
                    catchError((error: any) => {
                        if (error.error.code === 401 || error?.status === 401) {
                            this.authService.clearLoginInfo();
                            this.router.navigate([PATH_LOCATIONS.children[PATHS.LOGIN].path]);
                            throw error;
                        }
                        // error.error.formattedMessageId = 'organiser.error.generic';
                        throw error;
                    })
                );
        }
    }

    public logout(): Observable<any> {
        const _body: HttpsRequestBody = {
            id: parseInt((Math.random() * 100).toString(), 10),
            jsonrpc: JSONRPC,
            method: XAPI_METHODS[XAPIS.auth][XAPI_METHODS_NAMES.logout],
            params: {
                token: this.authService.getToken()
            }
        };

        return this.post(this.configService.getServerUrlXapi(), XAPIS.auth, _body).pipe(
            map((resp: any) => {
                if (resp?.result?.status === REQUEST_STATUSES.success) {
                    this.authService.clearLoginInfo();
                    this.router.navigate([PATH_LOCATIONS.children[PATHS.LOGIN].path]);
                    return resp.result;
                }
                this.authService.clearLoginInfo();
                this.router.navigate([PATH_LOCATIONS.children[PATHS.LOGIN].path]);
                throw resp;
            }),
            catchError((error) => {
                this.authService.clearLoginInfo();
                this.router.navigate([PATH_LOCATIONS.children[PATHS.LOGIN].path]);
                throw error;
            })
        );
    }
}
